import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  FileDown,
  FileText,
  Printer,
  Loader2,
  ChevronDown,
  ChevronUp,
  Edit3,
  Check,
  X,
  History,
  RotateCcw,
  Clock,
  Send,
  CheckCircle,
  FileSignature,
  Lock,
  AlertTriangle,
  Plus,
  Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  draft: {
    label: "Draft",
    color: "bg-stone-100 text-stone-700 border-stone-300",
    icon: Edit3,
    description: "Minutes are being drafted",
  },
  in_review: {
    label: "In Review",
    color: "bg-amber-100 text-amber-700 border-amber-300",
    icon: Clock,
    description: "Awaiting approval",
  },
  approved: {
    label: "Approved",
    color: "bg-blue-100 text-blue-700 border-blue-300",
    icon: CheckCircle,
    description: "Approved, pending signature",
  },
  signed: {
    label: "Signed",
    color: "bg-emerald-100 text-emerald-700 border-emerald-300",
    icon: FileSignature,
    description: "Signed by Chair",
  },
  locked: {
    label: "Locked",
    color: "bg-red-100 text-red-700 border-red-300",
    icon: Lock,
    description: "Immutable record",
  },
};

export default function MinutesEditorPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);
  
  // Workflow state
  const [workflowAction, setWorkflowAction] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signatureType, setSignatureType] = useState("typed");
  const [signatureName, setSignatureName] = useState("");
  const [approvalComments, setApprovalComments] = useState("");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const fetchData = async () => {
    try {
      const [meetingRes, minutesRes] = await Promise.all([
        axios.get(`${API}/meetings/${meetingId}`),
        axios.get(`${API}/meetings/${meetingId}/minutes`),
      ]);
      setMeeting(meetingRes.data);
      setMinutes(minutesRes.data);

      // Expand all sections by default
      const expanded = {};
      minutesRes.data.sections?.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Minutes not found. Generate minutes first.");
        navigate(`/meetings/${meetingId}`);
      } else {
        toast.error("Failed to load minutes");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isReadOnly) {
      toast.error("Cannot edit signed/locked minutes");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.put(`${API}/meetings/${meetingId}/minutes`, {
        sections: minutes.sections,
      });
      setMinutes({ ...minutes, version: response.data.version });
      toast.success("Minutes saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save minutes");
    } finally {
      setSaving(false);
    }
  };

  const fetchVersionHistory = async () => {
    setLoadingVersions(true);
    try {
      const response = await axios.get(`${API}/meetings/${meetingId}/minutes/versions`);
      setVersionHistory(response.data.history || []);
    } catch (error) {
      toast.error("Failed to load version history");
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleShowVersionHistory = () => {
    setShowVersionHistory(true);
    fetchVersionHistory();
  };

  const handleRestoreVersion = async (version) => {
    if (!window.confirm(`Are you sure you want to restore version ${version}? Your current version will be saved to history.`)) {
      return;
    }
    
    setRestoringVersion(true);
    try {
      const response = await axios.post(`${API}/meetings/${meetingId}/minutes/restore/${version}`);
      toast.success(`Restored version ${version}`);
      setShowVersionHistory(false);
      fetchData(); // Reload the minutes
    } catch (error) {
      toast.error("Failed to restore version");
    } finally {
      setRestoringVersion(false);
    }
  };

  // Workflow Actions
  const handleSubmitForReview = async () => {
    setWorkflowLoading(true);
    try {
      await axios.post(`${API}/meetings/${meetingId}/minutes/submit-review`);
      toast.success("Minutes submitted for review");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit for review");
    } finally {
      setWorkflowLoading(false);
      setWorkflowAction(null);
    }
  };

  const handleApprove = async () => {
    setWorkflowLoading(true);
    try {
      await axios.post(`${API}/meetings/${meetingId}/minutes/approve`, {
        comments: approvalComments,
      });
      toast.success("Minutes approved");
      setApprovalComments("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve minutes");
    } finally {
      setWorkflowLoading(false);
      setWorkflowAction(null);
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim()) {
      toast.error("Please enter your name for signature");
      return;
    }
    setWorkflowLoading(true);
    try {
      await axios.post(`${API}/meetings/${meetingId}/minutes/sign`, {
        signature_type: signatureType,
        signature_name: signatureName,
      });
      toast.success("Minutes signed successfully");
      setShowSignDialog(false);
      setSignatureName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign minutes");
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleLock = async () => {
    setWorkflowLoading(true);
    try {
      await axios.post(`${API}/meetings/${meetingId}/minutes/lock`);
      toast.success("Minutes locked. This is now an immutable record.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to lock minutes");
    } finally {
      setWorkflowLoading(false);
      setWorkflowAction(null);
    }
  };

  const handleCreateNewVersion = async () => {
    setWorkflowLoading(true);
    try {
      await axios.post(`${API}/meetings/${meetingId}/minutes/new-version`);
      toast.success("New draft version created");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create new version");
    } finally {
      setWorkflowLoading(false);
    }
  };

  const formatChangeType = (type) => {
    switch (type) {
      case 'regenerated': return 'Regenerated';
      case 'edited': return 'Manual Edit';
      case 'before_restore': return 'Before Restore';
      case 'new_version_created': return 'New Version';
      default: return type;
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/meetings/${meetingId}/export/${format}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${meeting?.name || "Minutes"}_Minutes.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const updateSectionContent = (index, content) => {
    if (isReadOnly) return;
    const newSections = [...minutes.sections];
    newSections[index] = { ...newSections[index], content };
    setMinutes({ ...minutes, sections: newSections });
  };

  const updateSectionTitle = (index, title) => {
    if (isReadOnly) return;
    const newSections = [...minutes.sections];
    newSections[index] = { ...newSections[index], title };
    setMinutes({ ...minutes, sections: newSections });
  };

  const toggleSection = (index) => {
    setExpandedSections({
      ...expandedSections,
      [index]: !expandedSections[index],
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Computed values
  const currentStatus = minutes?.status || "draft";
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const isReadOnly = currentStatus === "signed" || currentStatus === "locked";

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary-700 animate-spin" />
      </div>
    );
  }

  if (!minutes) return null;

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header - Hidden on print */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10 no-print">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/meetings/${meetingId}`)}
                className="text-stone-600"
                data-testid="back-to-meeting-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-200" />
              <div>
                <h1 className="font-heading text-lg font-semibold text-stone-900">
                  Minutes Editor
                </h1>
                <p className="text-xs text-stone-500">
                  Version {minutes.version}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleShowVersionHistory}
                data-testid="version-history-btn"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("docx")}
                disabled={exporting}
                data-testid="export-docx-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                DOCX
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("pdf")}
                disabled={exporting}
                data-testid="export-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </Button>

              {!isReadOnly && (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary-700 hover:bg-primary-700/90"
                  data-testid="save-minutes-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Status Ribbon */}
      <div className={`border-b ${statusConfig.color} no-print`} data-testid="status-ribbon">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <StatusIcon className="w-5 h-5" />
              <div>
                <span className="font-medium">{statusConfig.label}</span>
                <span className="text-sm ml-2 opacity-75">— {statusConfig.description}</span>
              </div>
            </div>
            
            {/* Workflow Actions */}
            <div className="flex items-center gap-2">
              {currentStatus === "draft" && (
                <Button
                  size="sm"
                  onClick={() => setWorkflowAction("submit")}
                  disabled={workflowLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="submit-for-review-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit for Review
                </Button>
              )}
              
              {currentStatus === "in_review" && (
                <Button
                  size="sm"
                  onClick={() => setWorkflowAction("approve")}
                  disabled={workflowLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              )}
              
              {currentStatus === "approved" && (
                <Button
                  size="sm"
                  onClick={() => setShowSignDialog(true)}
                  disabled={workflowLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="sign-btn"
                >
                  <FileSignature className="w-4 h-4 mr-2" />
                  Sign Minutes
                </Button>
              )}
              
              {currentStatus === "signed" && (
                <Button
                  size="sm"
                  onClick={() => setWorkflowAction("lock")}
                  disabled={workflowLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="lock-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Lock Minutes
                </Button>
              )}
              
              {(currentStatus === "signed" || currentStatus === "locked") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCreateNewVersion}
                  disabled={workflowLoading}
                  data-testid="new-version-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Version
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Read-only notice */}
      {isReadOnly && (
        <div className="bg-amber-50 border-b border-amber-200 no-print">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <Eye className="w-4 h-4" />
              <span>This document is {currentStatus}. Viewing in read-only mode.</span>
              {currentStatus === "locked" && minutes?.content_hash && (
                <Badge variant="outline" className="ml-2 font-mono text-xs">
                  Hash: {minutes.content_hash.substring(0, 12)}...
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Paper Document */}
        <div
          className="bg-white shadow-lg rounded-lg mx-auto"
          style={{ maxWidth: "850px" }}
        >
          {/* Document Header */}
          <div className="border-b border-stone-200 p-8 pb-6 text-center">
            <h1 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
              MINUTES OF MEETING
            </h1>
            <h2 className="font-heading text-xl text-stone-700 mb-4">
              {meeting?.name}
            </h2>
            <p className="text-stone-600">{meeting?.committee_name}</p>
            <div className="mt-4 inline-flex items-center gap-4 text-sm text-stone-500 font-mono">
              <span>{meeting?.date}</span>
              <span>|</span>
              <span>
                {meeting?.start_time} - {meeting?.end_time}
              </span>
              <span>|</span>
              <span>{meeting?.venue}</span>
            </div>
          </div>

          {/* Sections */}
          <div className="p-8 space-y-6" data-testid="minutes-sections">
            {minutes.sections.map((section, index) => (
              <div
                key={index}
                className="border border-stone-200 rounded-lg overflow-hidden"
                data-testid={`section-${index}`}
              >
                {/* Section Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 bg-stone-50 cursor-pointer"
                  onClick={() => toggleSection(index)}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-stone-400 bg-stone-200 px-2 py-0.5 rounded">
                      {section.section_number}
                    </span>
                    {editingSection === index && !isReadOnly ? (
                      <Input
                        value={section.title}
                        onChange={(e) => updateSectionTitle(index, e.target.value)}
                        className="font-heading font-medium text-stone-900 h-8"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`section-title-input-${index}`}
                      />
                    ) : (
                      <h3 className="font-heading font-medium text-stone-900">
                        {section.title}
                      </h3>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isReadOnly && (
                      editingSection === index ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSection(null);
                          }}
                        >
                          <Check className="w-4 h-4 text-emerald-600" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSection(index);
                          }}
                          data-testid={`edit-section-btn-${index}`}
                        >
                          <Edit3 className="w-4 h-4 text-stone-400" />
                        </Button>
                      )
                    )}
                    {expandedSections[index] ? (
                      <ChevronUp className="w-4 h-4 text-stone-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-stone-400" />
                    )}
                  </div>
                </div>

                {/* Section Content */}
                {expandedSections[index] && (
                  <div className="p-4">
                    {isReadOnly ? (
                      <div className="whitespace-pre-wrap text-stone-700 leading-relaxed font-body">
                        {section.content}
                      </div>
                    ) : (
                      <Textarea
                        value={section.content}
                        onChange={(e) => updateSectionContent(index, e.target.value)}
                        className="min-h-[150px] text-stone-700 leading-relaxed resize-y font-body"
                        placeholder="Enter section content..."
                        data-testid={`section-content-${index}`}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Signature Block */}
          <div className="border-t border-stone-200 p-8 pt-6">
            <div className="max-w-md">
              {minutes?.signed_at ? (
                <div className="space-y-3">
                  <div className="border-b-2 border-stone-900 pb-2">
                    <p className="text-lg font-medium italic text-stone-800">
                      {minutes.signer_name}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-sm text-stone-600">
                    <span>Chair Signature</span>
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Signed {new Date(minutes.signed_at).toLocaleDateString()}
                    </Badge>
                  </div>
                </div>
              ) : (
                <>
                  <div className="border-b border-stone-900 mb-2 h-10" />
                  <p className="text-sm text-stone-600">Chair Signature</p>
                  <p className="text-sm text-stone-500 mt-1 font-mono">
                    Date: {meeting?.date}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Approval Info */}
          {(minutes?.approved_at || minutes?.locked_at) && (
            <div className="border-t border-stone-100 p-6 bg-stone-50 text-sm space-y-2">
              {minutes.approved_at && (
                <p className="text-stone-600">
                  <span className="font-medium">Approved:</span> {new Date(minutes.approved_at).toLocaleString()}
                </p>
              )}
              {minutes.locked_at && (
                <p className="text-stone-600">
                  <span className="font-medium">Locked:</span> {new Date(minutes.locked_at).toLocaleString()}
                  {minutes.content_hash && (
                    <span className="ml-2 font-mono text-xs text-stone-400">
                      (Hash: {minutes.content_hash.substring(0, 16)}...)
                    </span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Print Footer */}
        <div className="print-only text-center mt-8 text-sm text-stone-500">
          Generated by MyMeetingsPal • Version {minutes?.version}
        </div>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body {
            background: white !important;
          }
          main {
            padding: 0 !important;
          }
        }
        .print-only {
          display: none;
        }
      `}</style>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Version History
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-4">
            {loadingVersions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-700" />
              </div>
            ) : versionHistory.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <History className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No previous versions found</p>
                <p className="text-sm mt-1">Edit or regenerate minutes to create version history</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Current Version */}
                <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-primary-700">Version {minutes?.version}</span>
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">Current</span>
                        {currentStatus !== "draft" && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {currentStatus.replace("_", " ")}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-500 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {minutes?.updated_at ? new Date(minutes.updated_at).toLocaleString() : new Date(minutes?.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Previous Versions */}
                {versionHistory.map((version) => (
                  <div key={version.id} className="p-4 border border-stone-200 rounded-lg hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-stone-700">Version {version.version}</span>
                          <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full">
                            {formatChangeType(version.change_type)}
                          </span>
                          {version.status && version.status !== "draft" && (
                            <Badge variant="outline" className="text-xs capitalize">
                              {version.status.replace("_", " ")}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-stone-500 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(version.archived_at).toLocaleString()}
                        </p>
                        <p className="text-xs text-stone-400 mt-1">
                          {version.sections?.length || 0} sections
                        </p>
                      </div>
                      {!isReadOnly && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreVersion(version.version)}
                          disabled={restoringVersion}
                          data-testid={`restore-version-${version.version}`}
                        >
                          {restoringVersion ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit for Review Confirmation */}
      <AlertDialog open={workflowAction === "submit"} onOpenChange={() => setWorkflowAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-500" />
              Submit Minutes for Review?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The minutes will be submitted for approval. You can still edit them while they&apos;re in review,
              but changes will need to be re-submitted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitForReview}
              disabled={workflowLoading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {workflowLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit for Review
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve Confirmation */}
      <AlertDialog open={workflowAction === "approve"} onOpenChange={() => setWorkflowAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-500" />
              Approve Minutes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Once approved, the minutes will be ready for signing by the Chair.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="text-sm text-stone-600">Approval Comments (Optional)</Label>
            <Textarea
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              placeholder="Add any comments..."
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApprove}
              disabled={workflowLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {workflowLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sign Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-emerald-500" />
              Sign Minutes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Signature Type</Label>
              <Select value={signatureType} onValueChange={setSignatureType}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="typed">Typed Signature</SelectItem>
                  <SelectItem value="drawn">Drawn Signature</SelectItem>
                  <SelectItem value="certificate">Digital Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Your Full Name</Label>
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Enter your full name as signature"
                className="mt-2"
              />
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              By signing, you confirm that these minutes accurately reflect the proceedings of the meeting.
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowSignDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSign}
              disabled={workflowLoading || !signatureName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {workflowLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign Minutes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lock Confirmation */}
      <AlertDialog open={workflowAction === "lock"} onOpenChange={() => setWorkflowAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Lock className="w-5 h-5" />
              Lock Minutes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-red-600">This action is irreversible.</strong> Locking the minutes will make them 
              an immutable record. A content hash will be generated to verify integrity. You will need to 
              create a new version if any changes are required.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLock}
              disabled={workflowLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {workflowLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Lock Minutes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
